/** Fleet-global dashboard pages (not bound to a coding machine). */
const FLEET_PAGES = new Set(["home", "machines", "providers", "usage", "automations", "tools", "settings", "login", "html", "artifact"])
const CONTROL_PLANE_DASHBOARD_PATHS = new Set([
  "/dashboard/home",
  "/dashboard/machines",
  "/dashboard/providers",
  "/dashboard/usage",
  "/dashboard/automations",
  "/dashboard/tools",
  "/dashboard/settings",
])

export function encodeMachineParam(name: string) {
  return encodeURIComponent(name)
}

function decodeMachineParam(value: string | undefined) {
  if (!value) return undefined
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** True when `segment` is a top-level fleet page, not a workspace slug. */
export function isFleetPageSegment(segment: string | undefined) {
  if (!segment) return false
  return FLEET_PAGES.has(segment)
}

/** Routes rendered entirely from control-plane state. */
export function isControlPlaneDashboardPath(pathname: string) {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname
  return normalized === "/dashboard" || CONTROL_PLANE_DASHBOARD_PATHS.has(normalized)
}

/** Render fleet-global pages without the machine app shell only when no machine exists. */
export function useStandaloneControlPlaneRoute(
  pathname: string,
  machineCount: number,
  _startedEmpty = false,
) {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname
  if (normalized === "/dashboard") return true
  if (CONTROL_PLANE_DASHBOARD_PATHS.has(normalized)) return machineCount === 0
  return false
}

/** Build the absolute hosted route for a machine destination. */
export function dashboardMachinePath(path: string) {
  if (path === "/dashboard" || path.startsWith("/dashboard/")) return path
  return path === "/" ? "/dashboard" : `/dashboard${path}`
}

/**
 * Parse machine name from a dashboard pathname.
 * Supports `/dashboard/m/:machine/...` and `/m/:machine/...`.
 */
export function parseWorkspaceFromPath(pathname: string): string | undefined {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean)
  let i = 0
  if (parts[0] === "dashboard") i = 1
  if (parts[i] !== "m" || !parts[i + 2]) return undefined
  try {
    return decodeURIComponent(parts[i + 2]!)
  } catch {
    return parts[i + 2]
  }
}

export function workspaceIDFromRoute(pathname: string, routeParam?: string): string | undefined {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean)
  const offset = parts[0] === "dashboard" ? 1 : 0
  if (parts[offset] === "m") return parseWorkspaceFromPath(pathname)
  return routeParam
}

export function parseSessionFromPath(pathname: string): string | undefined {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean)
  const session = parts.lastIndexOf("session")
  if (session === -1 || !parts[session + 1]) return undefined
  try {
    return decodeURIComponent(parts[session + 1]!)
  } catch {
    return parts[session + 1]
  }
}

export function parseMachineFromPath(pathname: string): string | undefined {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean)
  if (parts.length === 0) return undefined

  let i = 0
  if (parts[0] === "dashboard") i = 1
  if (parts[i] !== "m") return undefined
  return decodeMachineParam(parts[i + 1])
}

/** Remaining path after `/m/:machine` (always starts with `/` when non-empty). */
export function pathAfterMachine(pathname: string): string {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean)
  let i = 0
  if (parts[0] === "dashboard") i = 1
  if (parts[i] !== "m" || !parts[i + 1]) return pathname.startsWith("/") ? pathname : `/${pathname}`
  const rest = parts.slice(i + 2)
  return rest.length ? `/${rest.join("/")}` : "/"
}

export function isMachineScopedPath(pathname: string) {
  return parseMachineFromPath(pathname) !== undefined
}

/**
 * Workspace/session paths that must be machine-scoped.
 * Fleet pages and already-scoped `/m/...` paths are left alone.
 */
export function needsMachineScope(path: string) {
  if (!path.startsWith("/")) return false
  const bare = path.split("?")[0]?.split("#")[0] ?? path
  if (bare === "/" || bare === "") return true
  if (bare === "/dashboard" || bare.startsWith("/dashboard/")) {
    const after = bare === "/dashboard" ? "/" : bare.slice("/dashboard".length)
    return needsMachineScope(after)
  }
  if (bare === "/m" || bare.startsWith("/m/")) return false
  const first = bare.slice(1).split("/")[0] ?? ""
  if (isFleetPageSegment(first)) return false
  return true
}

/** Prefix a relative app path with `/m/:machine` when required. */
export function injectMachine(path: string, machine: string): string {
  if (!machine || !needsMachineScope(path)) return path

  const hashIndex = path.indexOf("#")
  const queryIndex = path.indexOf("?")
  let end = path.length
  if (hashIndex >= 0) end = Math.min(end, hashIndex)
  if (queryIndex >= 0) end = Math.min(end, queryIndex)
  const bare = path.slice(0, end)
  const suffix = path.slice(end)

  if (bare === "/dashboard") return `/m/${encodeMachineParam(machine)}${suffix}`
  if (bare.startsWith("/dashboard/")) {
    const rest = bare.slice("/dashboard".length)
    return `/m/${encodeMachineParam(machine)}${rest}${suffix}`
  }
  if (bare === "/") return `/m/${encodeMachineParam(machine)}${suffix}`
  return `/m/${encodeMachineParam(machine)}${bare}${suffix}`
}

export function machineHomePath(machine: string) {
  return `/m/${encodeMachineParam(machine)}`
}

/** A machine-relative chat route (a chat, a new chat, or an Oracle), including optional query/hash. */
export function isMachineChatRoute(route: string) {
  const pathname = route.split(/[?#]/, 1)[0] ?? ""
  return /^\/[^/]+\/(?:session(?:\/[^/]+)?|oracle)\/?$/.test(pathname)
}

/** Resume a machine's remembered chat, otherwise open its home. */
export function machineResumePath(machine: string, route?: string) {
  if (!route || !isMachineChatRoute(route)) return machineHomePath(machine)
  return `${machineHomePath(machine)}${route.startsWith("/") ? route : `/${route}`}`
}

export function machineSessionPath(input: {
  machine: string
  dir: string
  id?: string
  query?: string
  hash?: string
}) {
  const id = input.id ? `/${input.id}` : ""
  const query = input.query ?? ""
  const hash = input.hash ?? ""
  return `/m/${encodeMachineParam(input.machine)}/${input.dir}/session${id}${query}${hash}`
}

export function machineOraclePath(machine: string, dir: string, summary = false) {
  return `/m/${encodeMachineParam(machine)}/${dir}/oracle${summary ? "/summary" : ""}`
}
